import { vacationAllowanceRepository } from '../db/database';
import { REWARDS, type Reward } from '../models/types';
import { getOrCreateProgressState } from '../services/dailyRollover';
import { getDayCover, type DayCover } from '../services/dayCover';
import { getRewardViews, redeemReward, type RewardView } from '../services/rewards';
import { todayIso } from '../utils/date';
import { setDelegatedClick } from '../ui/delegatedClick';
import { renderIcon } from '../ui/icons';
import type { RouteId } from '../ui/tabBar';

/**
 * Ödüller ekranı — CLAUDE.md §7.6.
 *
 * Profil > Ödüller'den açılır. Kullanıcı biriken Başarı Puanı'nı
 * Day Streak gününe çevirir.
 */

/** Son kullanımdan sonra gösterilen kısa onay mesajı. */
let lastRedeemMessage: string | null = null;

function escapeHtml(text: string): string {
  const element = document.createElement('span');
  element.textContent = text;
  return element.innerHTML;
}

/**
 * Harcanabilir puan kartı.
 *
 * Bu sayı, Anasayfa'daki Başarı Barı'ndan farklı olabilir: burada
 * yalnızca kapanmış günün puanı gösterilir (§7.6). Kullanıcının
 * "ekranda 110 yazıyordu ama buton pasif" diye şaşırmaması için,
 * harcayabileceği tutarı doğrudan yazıyoruz.
 */
function renderBalance(spendablePoints: number, currentStreak: number): string {
  return `
    <section class="balance-card">
      <div class="balance-card__item">
        <p class="balance-card__label">Harcanabilir puan</p>
        <p class="balance-card__value">${spendablePoints}</p>
      </div>
      <div class="balance-card__item">
        <p class="balance-card__label">Day Streak</p>
        <p class="balance-card__value">
          ${renderIcon('flame', { size: 16 })}
          ${currentStreak}
        </p>
      </div>
    </section>
  `;
}

/**
 * Ödül kartı.
 *
 * §7.6 — Gün zaten dondurulmuşsa (tatil kullanıldıysa veya bugün
 * zaten ödülle satın alındıysa) düğme kapalıdır: aynı günü ikinci kez
 * güvenceye almak, hakkı ya da 100 puanı karşılıksız yakardı.
 */
function renderRewardCard(view: RewardView, cover: DayCover): string {
  const { reward, affordable } = view;
  // Puan yetmiyorsa ne kadar eksik olduğunu söylüyoruz; "yetersiz"
  // demek tek başına kullanıcıya ne yapması gerektiğini anlatmaz.
  const missing = reward.costInScore - view.spendablePoints;

  let action: string;
  if (cover !== 'none') {
    action = `<button type="button" class="reward-card__action reward-card__action--off" disabled>
                ${cover === 'vacation' ? 'Bugün tatil günü' : 'Bugün zaten alındı'}
              </button>`;
  } else if (affordable) {
    action = `<button type="button" class="reward-card__action" data-redeem="${reward.id}">
                Kullan
              </button>`;
  } else {
    action = `<button type="button" class="reward-card__action reward-card__action--off" disabled>
                ${missing} puan daha gerek
              </button>`;
  }

  return `
    <section class="reward-card">
      <h2 class="reward-card__title">${escapeHtml(reward.title)}</h2>
      <p class="reward-card__desc">${escapeHtml(reward.description)}</p>
      ${action}
    </section>
  `;
}

export async function renderRewardsPage(
  container: HTMLElement,
  navigate: (route: RouteId) => Promise<void>,
): Promise<void> {
  const [views, progress, allowance] = await Promise.all([
    getRewardViews(),
    getOrCreateProgressState(),
    vacationAllowanceRepository.get(),
  ]);

  const spendablePoints = views[0]?.spendablePoints ?? progress.barValue;
  const cover = getDayCover(todayIso(), allowance, progress);

  container.innerHTML = `
    <header class="detail-header">
      <button type="button" class="back-button" data-back aria-label="Profile dön">
        <svg width="8" height="14" viewBox="0 0 8 14" aria-hidden="true" focusable="false">
          <path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="2" fill="none"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h1 class="detail-header__title">Ödüller</h1>
    </header>

    ${renderBalance(spendablePoints, progress.currentStreak)}
    ${lastRedeemMessage ? `<p class="redeem-note">${escapeHtml(lastRedeemMessage)}</p>` : ''}
    ${views.map((view) => renderRewardCard(view, cover)).join('')}

    <div class="placeholder">
      <p class="placeholder__title">Yeni ödüller yakında</p>
    </div>
  `;

  // Mesaj yalnızca bir kez gösterilsin: ekran tekrar açıldığında eski
  // bir onay yazısı asılı kalmamalı.
  lastRedeemMessage = null;

  container.querySelector('[data-back]')?.addEventListener('click', () => {
    void navigate('profile');
  });

  // Sayfa her ödül kullanımından sonra kendini yeniden çiziyor.
  // `setDelegatedClick` önceki dinleyiciyi kaldırır — aksi halde
  // ikinci "Kullan" dokunuşu iki kez işlenir ve 100 puan yerine 200
  // puan düşerdi.
  setDelegatedClick('rewards', container, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest<HTMLElement>('[data-redeem]');
    if (!button) return;

    const reward: Reward | undefined = REWARDS.find(
      (candidate) => candidate.id === button.dataset['redeem'],
    );
    if (!reward) return;

    void (async () => {
      const result = await redeemReward(reward);

      if (result.ok) {
        // §7.6 — Ödül sayacı ANINDA artırmaz; bugünü satın alır ve gün
        // kapanışında sayaca girer. Doğal tamamlanan bir günde de böyle
        // olduğu için mesaj aynı dili kullanıyor.
        lastRedeemMessage =
          `Bugün satın alındı — seriye sayılacak. Kalan puan: ${result.newBarValue}.`;
      } else if (result.reason === 'alreadyCovered') {
        lastRedeemMessage = 'Bugün zaten güvencede; ikinci kez harcamaya gerek yok.';
      } else {
        lastRedeemMessage = 'Puanın yetmiyor.';
      }

      await renderRewardsPage(container, navigate);
    })();
  });
}
